import React, { useContext, useEffect, useState, useRef } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { Button } from '../../../../catalyst-components/button';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../catalyst-components/table';
import { ConfirmationDialog } from '../../../../catalyst-components/confirmation-dialog';
import { Id } from '../../../../convex/_generated/dataModel';
import { BaseContext } from '../../../../shared/base-context';
import { Input } from '../../../../catalyst-components/input';
import { Textarea } from '../../../../catalyst-components/textarea';
import { Field, Label } from '../../../../catalyst-components/fieldset';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../../catalyst-components/alert';
import { ArrowLeftIcon, PlusIcon, PencilIcon, TrashIcon, CheckCircleIcon, XCircleIcon, PhotoIcon } from '@heroicons/react/24/outline';

interface IntroSlide {
    _id: Id<"intro_slides">;
    title: string;
    body: string;
    image_id?: Id<"_storage">;
    cta_text?: string;
    sequence: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export const IntroSlides: React.FC = () => {
    const navigate = useNavigate();
    const { convexUserId } = useContext(BaseContext);
    const slides = useQuery(api.intro_slides.list) || [];
    const createSlide = useMutation(api.intro_slides.create);
    const updateSlide = useMutation(api.intro_slides.update);
    const deleteSlide = useMutation(api.intro_slides.remove);
    const toggleActive = useMutation(api.intro_slides.toggleActive);
    const generateUploadUrl = useMutation(api.intro_slides.generateUploadUrl);

    const [confirmDelete, setConfirmDelete] = useState<{
        open: boolean;
        slide?: IntroSlide;
    }>({ open: false });

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<Id<"intro_slides"> | null>(null);
    const [formData, setFormData] = useState({
        title: '',
        body: '',
        cta_text: '',
        sequence: 1,
    });
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [existingImageId, setExistingImageId] = useState<Id<"_storage"> | null>(null);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (editingId) {
            const slide = slides.find(s => s._id === editingId);
            if (slide) {
                setFormData({
                    title: slide.title,
                    body: slide.body,
                    cta_text: slide.cta_text || '',
                    sequence: slide.sequence,
                });
                setExistingImageId(slide.image_id || null);
                setImageFile(null);
                setImagePreview(null);
            }
        }
    }, [editingId, slides]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            setExistingImageId(null);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const uploadImage = async (): Promise<Id<"_storage"> | undefined> => {
        if (!imageFile) return existingImageId || undefined;

        const uploadUrl = await generateUploadUrl();
        const response = await fetch(uploadUrl, {
            method: 'POST',
            headers: { 'Content-Type': imageFile.type },
            body: imageFile,
        });
        const { storageId } = await response.json();
        return storageId;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.title.trim() || !formData.body.trim()) {
            setAlertMessage('Title and body are required');
            setShowAlert(true);
            return;
        }

        if (formData.sequence < 1) {
            setAlertMessage('Sequence must be at least 1');
            setShowAlert(true);
            return;
        }

        setIsSubmitting(true);

        try {
            const image_id = await uploadImage();

            if (editingId) {
                await updateSlide({
                    id: editingId,
                    title: formData.title,
                    body: formData.body,
                    cta_text: formData.cta_text || undefined,
                    sequence: formData.sequence,
                    image_id,
                    updated_by: convexUserId as Id<"users">,
                });
                setAlertMessage('Slide updated successfully!');
            } else {
                await createSlide({
                    title: formData.title,
                    body: formData.body,
                    cta_text: formData.cta_text || undefined,
                    sequence: formData.sequence,
                    image_id,
                    created_by: convexUserId as Id<"users">,
                });
                setAlertMessage('Slide created successfully!');
            }
            setShowAlert(true);
            resetForm();
        } catch (error) {
            setAlertMessage('Error saving slide. Please try again.');
            setShowAlert(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({ title: '', body: '', cta_text: '', sequence: slides.length + 1 });
        setEditingId(null);
        setShowForm(false);
        setImageFile(null);
        setImagePreview(null);
        setExistingImageId(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleEdit = (slide: IntroSlide) => {
        setEditingId(slide._id);
        setShowForm(true);
    };

    const handleToggleActive = async (id: Id<"intro_slides">) => {
        try {
            await toggleActive({
                id,
                updated_by: convexUserId as Id<"users">,
            });
        } catch (error) {
        }
    };

    return (
        <div className="min-h-screen bg-background pt-24">
            {/* Header */}
            <div className="border-b border-border/50 bg-gradient-to-r from-indigo-50 to-indigo-100/50 px-6 py-8">
                <div>
                    <div className="flex items-center gap-4 mb-4">
                        <Button
                            color="secondary"
                            className="rounded-full"
                            onClick={() => navigate('../')}
                        >
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Back
                        </Button>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-xl bg-white border-2 border-indigo-200">
                                <PhotoIcon className="h-8 w-8 text-primary" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-semibold text-foreground mb-1">Intro Slides</h1>
                                <p className="text-base text-muted-foreground">
                                    Manage slides shown to new users before onboarding
                                </p>
                            </div>
                        </div>
                        <Button
                            color="primary"
                            className="rounded-full"
                            onClick={() => {
                                resetForm();
                                setFormData(prev => ({ ...prev, sequence: slides.length + 1 }));
                                setShowForm(true);
                            }}
                        >
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Add New Slide
                        </Button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-6 py-8">
                <div className="space-y-6">
                    {showForm && (
                        <div className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-indigo-100/30 p-6 rounded-xl">
                            <h2 className="text-xl font-semibold text-foreground mb-4">
                                {editingId ? 'Edit Slide' : 'New Slide'}
                            </h2>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Field>
                                        <Label>Title</Label>
                                        <Input
                                            required
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            placeholder="Slide title..."
                                        />
                                    </Field>

                                    <Field>
                                        <Label>Sequence</Label>
                                        <Input
                                            required
                                            type="number"
                                            min={1}
                                            value={formData.sequence}
                                            onChange={(e) => setFormData({ ...formData, sequence: parseInt(e.target.value) || 1 })}
                                        />
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Display order (1 = first slide shown)
                                        </p>
                                    </Field>
                                </div>

                                <Field>
                                    <Label>Body</Label>
                                    <Textarea
                                        required
                                        rows={4}
                                        value={formData.body}
                                        onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                                        placeholder="Slide body text..."
                                    />
                                </Field>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Field>
                                        <Label>CTA Button Text (optional)</Label>
                                        <Input
                                            value={formData.cta_text}
                                            onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                                            placeholder='e.g. "Next", "Get Started"'
                                        />
                                    </Field>

                                    <Field>
                                        <Label>Image (optional)</Label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="block w-full text-sm text-muted-foreground
                                                    file:mr-4 file:py-2 file:px-4
                                                    file:rounded-full file:border-0
                                                    file:text-sm file:font-semibold
                                                    file:bg-primary/10 file:text-primary
                                                    hover:file:bg-primary/20"
                                            />
                                        </div>
                                        {imagePreview && (
                                            <img src={imagePreview} alt="Preview" className="mt-2 h-20 w-auto rounded-lg border border-border/50" />
                                        )}
                                        {!imagePreview && existingImageId && (
                                            <p className="text-sm text-muted-foreground mt-1">Current image will be kept</p>
                                        )}
                                    </Field>
                                </div>

                                <div className="flex gap-3">
                                    <Button type="submit" color="primary" className="rounded-full" disabled={isSubmitting}>
                                        {isSubmitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
                                    </Button>
                                    <Button type="button" color="secondary" className="rounded-full" onClick={resetForm}>
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}

                    <div className="border-2 border-border/50 rounded-xl overflow-hidden bg-card">
                        <Table>
                            <TableHead>
                                <TableRow className="bg-muted/30">
                                    <TableHeader>Seq</TableHeader>
                                    <TableHeader>Title</TableHeader>
                                    <TableHeader>Body</TableHeader>
                                    <TableHeader>Image</TableHeader>
                                    <TableHeader>CTA</TableHeader>
                                    <TableHeader>Status</TableHeader>
                                    <TableHeader>Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {slides.map((slide) => (
                                    <SlideRow
                                        key={slide._id}
                                        slide={slide}
                                        onEdit={handleEdit}
                                        onToggleActive={handleToggleActive}
                                        onDelete={(s) => setConfirmDelete({ open: true, slide: s })}
                                    />
                                ))}
                                {slides.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                                            No intro slides found. Add one to get started.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>

            <ConfirmationDialog
                open={confirmDelete.open}
                onClose={() => setConfirmDelete({ open: false })}
                onConfirm={async () => {
                    if (!confirmDelete.slide) return;
                    try {
                        await deleteSlide({ id: confirmDelete.slide._id });
                        setConfirmDelete({ open: false });
                    } catch (error) {
                    }
                }}
                title="Delete Slide"
                description="Are you sure you want to delete this intro slide? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                isCritical={true}
            />

            <Alert open={showAlert} onClose={() => setShowAlert(false)} size="lg">
                <AlertTitle>Notification</AlertTitle>
                <AlertDescription>{alertMessage}</AlertDescription>
                <AlertActions>
                    <Button plain onClick={() => setShowAlert(false)}>
                        Ok
                    </Button>
                </AlertActions>
            </Alert>
        </div>
    );
};

// Separate row component to allow per-row image URL queries
const SlideRow: React.FC<{
    slide: IntroSlide;
    onEdit: (slide: IntroSlide) => void;
    onToggleActive: (id: Id<"intro_slides">) => void;
    onDelete: (slide: IntroSlide) => void;
}> = ({ slide, onEdit, onToggleActive, onDelete }) => {
    const imageUrl = useQuery(
        api.intro_slides.getImageUrl,
        slide.image_id ? { image_id: slide.image_id } : 'skip'
    );

    return (
        <TableRow className="hover:bg-muted/20">
            <TableCell className="font-semibold text-foreground">
                {slide.sequence}
            </TableCell>
            <TableCell className="font-medium text-foreground max-w-[200px] truncate">
                {slide.title}
            </TableCell>
            <TableCell className="max-w-xs truncate text-muted-foreground">
                {slide.body}
            </TableCell>
            <TableCell>
                {imageUrl ? (
                    <img src={imageUrl} alt={slide.title} className="h-10 w-10 rounded object-cover border border-border/50" />
                ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                )}
            </TableCell>
            <TableCell className="text-muted-foreground">
                {slide.cta_text || '—'}
            </TableCell>
            <TableCell>
                {slide.is_active ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-full">
                        <CheckCircleIcon className="h-4 w-4" />
                        Active
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-muted-foreground bg-muted/30 border border-border/50 rounded-full">
                        <XCircleIcon className="h-4 w-4" />
                        Inactive
                    </span>
                )}
            </TableCell>
            <TableCell>
                <div className="flex gap-2">
                    <Button
                        color="secondary"
                        className="rounded-full"
                        onClick={() => onEdit(slide)}
                    >
                        <PencilIcon className="h-4 w-4" />
                    </Button>
                    <Button
                        color={slide.is_active ? "secondary" : "primary"}
                        className="rounded-full"
                        onClick={() => onToggleActive(slide._id)}
                    >
                        {slide.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                        color="red"
                        className="rounded-full"
                        onClick={() => onDelete(slide)}
                    >
                        <TrashIcon className="h-4 w-4" />
                    </Button>
                </div>
            </TableCell>
        </TableRow>
    );
};
